import type { ServiceResponse, ApiErrorResponse, UUID } from '@dopomogai/types';
import { handleApiError } from '../utils/handleApiError';
import { getApiClient, getRawApiBaseUrl } from '../client';

export type WidgetInitRequest = {
  installation_id?: string;
  widget_id?: string;
  template_id?: string;
  visitor_id?: string; // NEW
};

export type WidgetInitResponse = {
  token: string;
  ttl: number;
  expUtc: number;
  agentId: string;
  apiBaseUrl: string;
  versionId: string;
  installationId?: string;
  config?: Record<string, any>;
};

export type WidgetSuggestionsScope = 'new_conversation' | 'next_message';

export type WidgetSuggestionsResponse = {
  suggestions: Array<{ id?: string; title?: string; prompt: string }>;
};

export const widgetsService = {
  initSession: (rootApiUrl: string, body: WidgetInitRequest): ServiceResponse<WidgetInitResponse> => {
    const base = rootApiUrl || getRawApiBaseUrl();
    if (!base) {
      const error = { isError: true, message: 'API base URL not configured for widget init.', status: 500 } as ApiErrorResponse;
      return Promise.resolve(error);
    }
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const url = `${cleanBase}/api/v1/widgets/init-session`;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        if (!res.ok) {
          const detail = await res.text().catch(() => res.statusText);
          throw { response: { status: res.status, data: { detail } } };
        }
        return res.json();
      })
      .catch((err) => handleApiError(err, 'initializing widget session'));
  },

  /**
   * Convenience: init a visitor widget session using the globally configured base URL.
   * (Good for widget bundles where rootApiUrl isn't passed around.)
   */
  initVisitorSession: (params: { widgetVersionId: UUID; visitorId: string }): ServiceResponse<WidgetInitResponse> => {
    const base = getRawApiBaseUrl();
    return widgetsService.initSession(base, { widget_id: String(params.widgetVersionId), visitor_id: params.visitorId });
  },

  /**
   * Visitor-safe suggestions for new conversation.
   * Backend route:
   *   GET /api/v1/widgets/{widget_version_id}/suggestions?limit=4&force=false&persist=false
   * Auth:
   *   widget_session WAT (axios interceptor must attach Authorization)
   */
  getWidgetSuggestions: async (
    widgetVersionId: UUID,
    opts?: { limit?: number; force?: boolean; persist?: boolean }
  ): ServiceResponse<WidgetSuggestionsResponse> => {
    try {
      const limit = Math.max(1, Math.min(opts?.limit ?? 4, 8));
      const force = !!opts?.force;
      const persist = !!opts?.persist;

      const res = await getApiClient()
        .get<WidgetSuggestionsResponse>(`/widgets/${String(widgetVersionId)}/suggestions`, {
          params: { limit, force, persist },
        })
        .then((r) => r.data);

      return res;
    } catch (err) {
      return handleApiError(err, 'fetching widget suggestions');
    }
  },

  /**
   * Visitor-safe suggestions for next message in a conversation.
   * Backend route:
   *   GET /api/v1/widgets/{widget_version_id}/conversations/{conversation_id}/suggestions?limit=4
   */
  getWidgetConversationSuggestions: async (
    widgetVersionId: UUID,
    conversationId: UUID | string,
    opts?: { limit?: number; force?: boolean; persist?: boolean }
  ): ServiceResponse<WidgetSuggestionsResponse> => {
    try {
      const limit = Math.max(1, Math.min(opts?.limit ?? 4, 8));
      const force = !!opts?.force;
      const persist = !!opts?.persist;

      const res = await getApiClient()
        .get<WidgetSuggestionsResponse>(`/widgets/${String(widgetVersionId)}/conversations/${String(conversationId)}/suggestions`, {
          params: { limit, force, persist },
        })
        .then((r) => r.data);

      return res;
    } catch (err) {
      return handleApiError(err, 'fetching widget conversation suggestions');
    }
  },
};
